import React, { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import html from 'html-react-parser'
import { useRouter } from 'next/router'

import { css } from '@emotion/react'

import { Grid, mediaColumns } from '@/styles/grid'
import { Text1, Text2, Link1, Text1Styles } from '@/styles/typography'
import { mediaUp } from '@/styles/media'
import LangLink from '@/components/common/LangLink'
import { useMarket } from '@/utils/multi-markets/context'
import { DOCUMENTS, DEFAULT_COLORS } from '@/constants'
import { setCssVars } from '@/utils/common'
import { useFetchCustomizedClients } from '@/service/apiHooks/useFetchCustomizedClients'
import { focusVisible, underlineHover } from '@/styles/mixins'

import Link from './common/Link'

const AeCopyrightsText = html('NaviParking DMCC')
const BhCopyrightsText = html('Naviparking MENA W.L.L')
const PlCopyrightsText = html('Naviparking Poland Sp. z o.o.')

interface Props {
  short?: boolean
}

const components = {
  bh: BhCopyrightsText,
  ae: AeCopyrightsText,
  pl: PlCopyrightsText
}

export default function Footer({ short = false }: Props) {
  const { t } = useTranslation('common')
  const footerDate = new Date()
  const { locale, market } = useMarket()
  const CopyrightsText = components[market]
  const router = useRouter()
  const { id } = router.query as any
  const { clientDetails } = useFetchCustomizedClients({ id })

  useEffect(() => {
    const colors = clientDetails?.colorScheme ?? DEFAULT_COLORS
    setCssVars(colors)
  }, [clientDetails])

  return (
    <footer css={container} data-test-id="footer-newsletter">
      {!short && (
        <Grid>
          <nav css={navigationContainer}>
            <div css={groupContainer}>
              <div css={navigationColumn} data-test-id="footer-portal">
                <Text1 css={navigationGroupTitle} as="strong">
                  {t('footer.portal')}
                </Text1>
                <ul css={linksList}>
                  <li css={pathLink}>
                    <Link
                      href={{ pathname: '/parking-search' }}
                      passHref
                      prefetch={false}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.search')}
                    </Link>
                  </li>
                  <li css={pathLink}>
                    <Link
                      href={{ pathname: '/navipay' }}
                      passHref
                      prefetch={false}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.navipay')}
                    </Link>
                  </li>
                </ul>
              </div>
              <div css={navigationColumn} data-test-id="footer-portal-2">
                <div css={placeholderTitle} aria-hidden="true" />
                <ul css={linksList}>
                  <li css={pathLink}>
                    <Link
                      href={{ pathname: '/faq' }}
                      passHref
                      prefetch={false}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.faq')}
                    </Link>
                  </li>
                  <li css={pathLink}>
                    <Link
                      href={{ pathname: '/case-studies' }}
                      passHref
                      prefetch={false}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.landings')}
                    </Link>
                  </li>
                </ul>
              </div>
              <div css={navigationColumn} data-test-id="footer-portal-3">
                <div css={placeholderTitle} aria-hidden="true" />
                <ul css={linksList}>
                  <li css={pathLink}>
                    <Link
                      href={`https://blog.digitalparking.city/${
                        locale === 'pl' ? locale : ''
                      }`}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.blog')}
                    </Link>
                  </li>
                  <li css={pathLink}>
                    <Link
                      href={{ pathname: '/contact' }}
                      passHref
                      prefetch={false}
                      isCustomized
                      legacy={false}
                    >
                      {t('navigation.contact')}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div css={groupContainer}>
              <div css={navigationColumn} data-test-id="footer-community">
                <Text1 css={navigationGroupTitle} as="strong">
                  {t('footer.community')}
                </Text1>
                <ul css={linksList}>
                  <li>
                    <Link1
                      href={t('socials.facebook')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('footer.facebook')}
                    </Link1>
                  </li>
                  <li>
                    <Link1
                      href={t('socials.linkedin')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('footer.linkedin')}
                    </Link1>
                  </li>
                </ul>
              </div>
              <div css={navigationColumn} data-test-id="footer-community-2">
                <div css={placeholderTitle} aria-hidden="true" />
                <ul css={linksList}>
                  <li>
                    <Link1
                      href={
                        market === 'pl'
                          ? t('socials.instagram')
                          : 'https://www.instagram.com/naviparking/'
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('footer.instagram')}
                    </Link1>
                  </li>
                </ul>
              </div>
            </div>
            <div css={groupContainer}>
              <div css={navigationColumn} data-test-id="footer-terms">
                <Text1 css={navigationGroupTitle} as="strong">
                  {t('footer.terms')}
                </Text1>
                <ul css={linksList}>
                  <li>
                    <Link1
                      target="_blank"
                      href={DOCUMENTS.terms[market][locale]}
                    >
                      {t('footer.termsOfUse')}
                    </Link1>
                  </li>
                  <li>
                    <Link1
                      target="_blank"
                      href={DOCUMENTS.privacyPolicy[market][locale]}
                    >
                      {t('footer.privacyPolicy')}
                    </Link1>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </Grid>
      )}
      <Grid>
        {router.pathname.includes('legia') && (
          <div css={copyrightsContainer}>
            <Link1 href="https://cdn.naviparking.com/docs/regulamin-konkursu-legia.docx.pdf">
              <Text2 css={copyrightsText}>Regulamin Konkursu Legia</Text2>
            </Link1>
          </div>
        )}
        <div css={divider} />
        <div css={copyrightsContainer}>
          <Text2 css={copyrightsText}>
            © {footerDate.getFullYear()} {CopyrightsText}
          </Text2>
          <LangLink short />
        </div>
      </Grid>
    </footer>
  )
}

const pathLink = css`
  ${focusVisible};
  ${Text1Styles};
  ${underlineHover};
  color: var(--text-color);
  &:hover {
    color: var(--hover-color);
    text-decoration: underline;
  }
  &:focus {
    outline: 2px solid var(--focus-outline-color);
  }
  &:active {
    color: var(--active-color);
  }
  html[lang^='ar'] & {
    text-align: right;
    direction: rtl;
  }
`

const container = css`
  flex-shrink: 0;
  background-color: var(--tint);
`

const divider = css`
  ${mediaColumns({ xs: [12, 0] })};
  height: 2px;
  width: 100%;
  background-color: var(--main);
  opacity: 0.08;
  margin-top: 40px;
  ${mediaUp('lg')} {
    margin-top: 60px;
  }
`

const navigationContainer = css`
  ${mediaColumns({ xs: [12, 0] })};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  ${mediaUp('md')} {
    padding: 0 20px;
    margin-top: 40px;
  }
  ${mediaUp('lg')} {
    margin: 80px 0 20px;
    justify-content: space-between;
  }
`

const groupContainer = css`
  display: flex;
  gap: 36px;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-left: 10px;
`

const navigationColumn = css`
  max-width: 100%;
  text-align: start;
  margin-top: 18px;
  ${mediaUp('lg')} {
    text-align: left;
    margin-top: 0;
  }
  html[lang^='ar'] & {
    text-align: right;
    direction: rtl;
  }
`

const navigationGroupTitle = css`
  display: inline-flex;
  margin-bottom: 10px;
  ${mediaUp('lg')} {
    margin-bottom: 20px;
  }
`

const placeholderTitle = css`
  display: inline-flex;
  margin-bottom: 10px;
  height: 1em;
  line-height: 1.5;
  visibility: hidden;
  ${mediaUp('lg')} {
    margin-bottom: 28px;
  }
`

const linksList = css`
  list-style: none;
  li:not(:last-child) {
    ${mediaUp('lg')} {
      margin-bottom: 12px;
    }
  }
  a {
    display: inline-block;
    padding: 2px 0;
    ${mediaUp('lg')} {
      padding: 0;
    }
  }
  html[lang^='ar'] & {
    text-align: right;
    direction: rtl;
  }
`

const copyrightsContainer = css`
  ${mediaColumns({ xs: [12, 0] })};
  margin-bottom: 40px;
  ${mediaUp('lg')} {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 60px;
  }
`

const copyrightsText = css`
  text-align: center;
  color: var(--main);
  margin-top: 8px;
  ${mediaUp('lg')} {
    margin-top: 22px;
  }
`
